<!-- Backdrop -->
<div class="no-print fixed inset-0 z-[55] bg-neutral-950/50 backdrop-blur-[2px]" (click)="closed.emit()"></div>

<!-- Slide-over -->
<aside class="no-print fixed inset-y-0 right-0 z-[56] w-full max-w-[640px] bg-gray-50 shadow-2xl flex flex-col" role="dialog" aria-label="Orders by status">

  <!-- Header -->
  <div class="text-white px-6 pt-5 pb-5 flex-shrink-0 bg-gradient-to-br from-neutral-900 to-neutral-700">
    <div class="flex items-start justify-between gap-3">
      <div>
        <h2 class="text-xl font-bold tracking-tight">{{ status === 'ALL' ? 'All orders' : label(status) + ' orders' }}</h2>
        <p class="text-sm text-neutral-300 mt-0.5">Placed {{ rangeLong }}, by their status now</p>
      </div>
      <button class="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0" (click)="closed.emit()" aria-label="Close"><mat-icon>close</mat-icon></button>
    </div>
    <div class="grid grid-cols-3 gap-2 mt-4">
      <div class="rounded-xl bg-white/10 px-3 py-2"><div class="text-xl font-bold leading-tight">{{ count | number }}</div><div class="text-[10px] uppercase tracking-wider text-neutral-300 truncate">{{ status === 'ALL' ? 'Orders' : label(status) }}</div></div>
      <div class="rounded-xl bg-white/10 px-3 py-2"><div class="text-xl font-bold leading-tight text-amber-300">£{{ shownValue | number:'1.0-0' }}</div><div class="text-[10px] uppercase tracking-wider text-neutral-300">Value</div></div>
      <div class="rounded-xl bg-white/10 px-3 py-2"><div class="text-xl font-bold leading-tight">{{ allCount | number }}</div><div class="text-[10px] uppercase tracking-wider text-neutral-300">All placed</div></div>
    </div>
  </div>

  <!-- Filters -->
  <div class="bg-white border-b border-gray-200 px-6 py-3 flex flex-col gap-3 flex-shrink-0">
    <div class="inline-flex rounded-lg bg-gray-100 p-0.5 self-start flex-wrap">
      @for (r of ranges; track r.key) {
      <button class="px-3 py-1 rounded-md text-xs font-semibold transition-all" [class]="range === r.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'" (click)="setRange(r.key)">{{ r.label }}</button>
      }
    </div>
    <div class="flex flex-wrap gap-1.5">
      <button class="flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold transition-all"
        [class]="status === 'ALL' ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'"
        (click)="setStatus('ALL')">All <span class="tabular-nums opacity-70">{{ allCount }}</span></button>
      @for (t of tabs; track t.status) {
      <button class="flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold transition-all"
        [class]="status === t.status ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'"
        (click)="setStatus(t.status)">
        <span class="h-2 w-2 rounded-full" [class]="tone(t.status).dot"></span>{{ label(t.status) }} <span class="tabular-nums opacity-70">{{ t.count }}</span>
      </button>
      }
    </div>
    <div class="relative">
      <mat-icon class="absolute left-3 top-1/2 -translate-y-1/2 !text-gray-400 !text-[18px] !h-[18px] !w-[18px] pointer-events-none">search</mat-icon>
      <input type="text" placeholder="Search name, email, phone or order number"
        class="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
        [(ngModel)]="search" (ngModelChange)="onSearch()" />
    </div>
  </div>

  <!-- List -->
  <div class="flex-1 overflow-y-auto px-6 py-4">
    @if (loading) {
      <div class="h-64 flex flex-col items-center justify-center"><mat-spinner diameter="30"></mat-spinner><div class="text-sm text-gray-500 mt-3">Loading orders&hellip;</div></div>
    } @else if (loadError) {
      <div class="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm flex items-center justify-between"><span>Could not load orders.</span><button class="text-xs uppercase font-semibold underline" (click)="load(true)">Try again</button></div>
    } @else if (items.length === 0) {
      <div class="h-64 flex flex-col items-center justify-center text-center">
        <span class="h-14 w-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3"><mat-icon class="!text-gray-400 !text-[30px] !h-[30px] !w-[30px]">inbox</mat-icon></span>
        <div class="text-lg font-semibold text-gray-800">No {{ status === 'ALL' ? '' : label(status).toLowerCase() + ' ' }}orders {{ rangeLong }}</div>
        <div class="text-sm text-gray-500 mt-1">Try another status or a longer period above.</div>
      </div>
    } @else {
      <div class="flex flex-col gap-3">
        @for (o of items; track o.orderId) {
        <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 hover:border-amber-200 transition-colors">

          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-bold text-gray-900 truncate">{{ o.customer.name }}@if (o.customer.guest) { <span class="font-medium text-gray-400"> (Guest)</span> }</span>
                <span class="text-[10px] font-semibold px-2 py-0.5 rounded-full border" [class]="tone(o.status).pill">{{ label(o.status) }}</span>
              </div>
              @if (o.customer.company && o.customer.company !== o.customer.name) { <div class="text-xs text-gray-500 truncate">{{ o.customer.company }}</div> }
              <div class="mt-1 text-xs text-gray-500 flex items-center gap-1.5 flex-wrap">
                <button class="font-semibold text-amber-700 hover:underline" (click)="openOrder(o)">#{{ o.orderNo }}</button>
                <span>·</span><span>placed {{ o.orderDate | date:whenFormat }}</span>
                <span>·</span><span>{{ o.agent ? 'by ' + o.agent : 'website' + (o.origin ? ' (' + originLabel(o.origin) + ')' : '') }}</span>
              </div>
            </div>
            <div class="text-right flex-shrink-0">
              <div class="text-lg font-bold text-gray-900 leading-tight">£{{ o.amount | number:'1.2-2' }}</div>
              <div class="text-[11px] text-gray-500">{{ o.itemCount }} item{{ o.itemCount === 1 ? '' : 's' }}</div>
            </div>
          </div>

          @if (o.items.length) {
          <div class="mt-3 flex flex-wrap gap-1">
            @for (n of o.items; track $index) { <span class="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 max-w-full truncate">{{ n }}</span> }
            @if (o.itemCount > o.items.length) { <span class="text-[11px] text-gray-400 px-1">+{{ o.itemCount - o.items.length }} more</span> }
          </div>
          }

          <div class="mt-3 pt-3 border-t border-gray-100 flex items-center gap-x-3 gap-y-2 flex-wrap text-[11px] text-gray-500">
            <span class="flex items-center gap-1" [matTooltip]="o.paymentDate ? 'Paid ' + (o.paymentDate | date:'d MMM, HH:mm') : 'Not paid yet'">
              <mat-icon class="!text-[14px] !h-[14px] !w-[14px]" [class]="o.paymentDate ? 'text-emerald-600' : 'text-gray-400'">payments</mat-icon>{{ o.paymentMethod }}
            </span>
            @if (o.deliveryMethod) { <span class="flex items-center gap-1"><mat-icon class="!text-[14px] !h-[14px] !w-[14px]">local_shipping</mat-icon>{{ o.deliveryMethod }}</span> }
            @if (o.postCode) { <span class="flex items-center gap-1"><mat-icon class="!text-[14px] !h-[14px] !w-[14px]">location_on</mat-icon>{{ o.postCode }}</span> }
            <span class="flex-1"></span>
            @if (o.customer.phone) {
            <a class="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold hover:bg-emerald-100" [href]="'tel:' + o.customer.phone"><mat-icon class="!text-[14px] !h-[14px] !w-[14px]">call</mat-icon>{{ o.customer.phone }}</a>
            }
            <button class="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-gray-700 font-semibold hover:border-amber-300 hover:bg-amber-50" (click)="openOrder(o)">Open <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">arrow_forward</mat-icon></button>
          </div>
        </div>
        }

        @if (hasMore) {
        <button class="mx-auto mt-1 px-5 py-2.5 rounded-xl bg-white border border-gray-200 text-sm font-semibold text-gray-700 hover:border-amber-300 hover:bg-amber-50 transition-colors" [disabled]="loadingMore" (click)="loadMore()">
          {{ loadingMore ? 'Loading...' : 'Show more (' + (count - items.length) + ' left)' }}
        </button>
        }
      </div>
    }
  </div>
</aside>
